import React, { Component } from 'react';

/* ─── Google Places loader ───────────────────────────────────────────────────
   Dynamically loads the Maps JS API (Places library) the first time this
   component mounts. Uses REACT_APP_GOOGLE_MAPS_API_KEY from the environment.
   Predictions are restricted to regions/cities (types: ['(regions)']) so
   exact street addresses are never shown.
   ─────────────────────────────────────────────────────────────────────────── */

/* let placesApiLoaded = false;
let placesApiLoading = false;
const placesApiCallbacks = [];

function loadPlacesApi(apiKey) {
	return new Promise((resolve, reject) => {
		if (placesApiLoaded) return resolve();
		placesApiCallbacks.push({ resolve, reject });
		if (placesApiLoading) return;
		placesApiLoading = true;

		window.__googleMapsApiLoaded = () => {
			placesApiLoaded = true;
			placesApiLoading = false;
			placesApiCallbacks.forEach(cb => cb.resolve());
			placesApiCallbacks.length = 0;
		};

		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__googleMapsApiLoaded`;
		script.async = true;
		script.defer = true;
		script.onerror = (err) => {
			placesApiLoading = false;
			placesApiCallbacks.forEach(cb => cb.reject(err));
			placesApiCallbacks.length = 0;
		};
		document.head.appendChild(script);
	});
} */

/* Determine our category from Google's place types array */
function getCategoryFromTypes(types = []) {
	if (types.includes('lodging')) return 'hotel';
	if (types.includes('hotel')) return 'hotel';
	if (types.includes('country')) return 'country';
	if (types.includes('administrative_area_level_1')) return 'region';
	if (types.includes('administrative_area_level_2')) return 'city';
	if (types.includes('locality')) return 'city';
	if (types.includes('sublocality')) return 'city';
	if (types.includes('colloquial_area')) return 'region';
	// Default for any other place returned by (regions) filter
	return 'city';
}

class LocationsStep extends Component {
	constructor(props) {
		super(props);
		this.state = {
			searchTerm: '',
			showSuggestions: false,
			filteredSuggestions: [],
			apiError: false
		};
		this.autocompleteService = null;
		this.placesService = null;
		this.searchDebounce = null;
		this.googleReadyInterval = null;
		this.googleReadyAttempts = 0;
	}

	componentDidMount() {
		if (this.initAutocomplete()) return;

		this.googleReadyInterval = setInterval(() => {
			this.googleReadyAttempts += 1;
			if (this.initAutocomplete()) {
				clearInterval(this.googleReadyInterval);
				this.googleReadyInterval = null;
				return;
			}
			if (this.googleReadyAttempts >= 20) {
				clearInterval(this.googleReadyInterval);
				this.googleReadyInterval = null;
				this.setState({ apiError: true });
			}
		}, 300);
	}

	componentWillUnmount() {
		clearTimeout(this.searchDebounce);
		if (this.googleReadyInterval) {
			clearInterval(this.googleReadyInterval);
			this.googleReadyInterval = null;
		}
	}

	initAutocomplete() {
		if (window.google?.maps?.places) {
			this.autocompleteService = new window.google.maps.places.AutocompleteService();
			this.placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
			if (this.state.apiError) {
				this.setState({ apiError: false });
			}
			return true;
		}
		return false;
	}

	handleSearch = (e) => {
		const term = e.target.value;
		this.setState({ searchTerm: term });

		clearTimeout(this.searchDebounce);

		if (!term.trim()) {
			this.setState({ showSuggestions: false, filteredSuggestions: [] });
			return;
		}

		if (this.autocompleteService) {
			// Debounce 250 ms to avoid excessive API calls
			this.searchDebounce = setTimeout(() => {
				const fetchPredictions = (request) => new Promise((resolve) => {
					this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
						if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
							resolve([]);
							return;
						}
						resolve(predictions);
					});
				});

				Promise.all([
					fetchPredictions({ input: term, types: ['(regions)'] }),
					fetchPredictions({ input: term, types: ['establishment'] })
				]).then(([regionPredictions, hotelPredictions]) => {
					const byPlaceId = new Map();
					[...regionPredictions, ...hotelPredictions].forEach((prediction) => {
						if (!byPlaceId.has(prediction.place_id)) {
							byPlaceId.set(prediction.place_id, prediction);
						}
					});

					const apiSuggestions = Array.from(byPlaceId.values())
						.map(p => ({
							type: getCategoryFromTypes(p.types),
							value: p.description,
							placeId: p.place_id
						}))
						.filter(s => ['region', 'country', 'city', 'hotel'].includes(s.type));

					const lower = term.toLowerCase();
					const fallbackMatches = [
						...FALLBACK_REGIONS.filter(r => r.toLowerCase().includes(lower)).map(r => ({ type: 'region', value: r })),
						...FALLBACK_COUNTRIES.filter(c => c.toLowerCase().includes(lower)).map(c => ({ type: 'country', value: c }))
					];

					const apiValues = new Set(apiSuggestions.map(s => s.value.toLowerCase()));
					const merged = [
						...fallbackMatches.filter(f => !apiValues.has(f.value.toLowerCase())),
						...apiSuggestions
					].slice(0, 8);

					this.setState({
						filteredSuggestions: merged,
						showSuggestions: merged.length > 0
					});
				});
			}, 250);
		} else {
			// Fallback: filter built-in list
			const lower = term.toLowerCase();
			const suggestions = [
				...FALLBACK_REGIONS.filter(r => r.toLowerCase().includes(lower)).map(r => ({ type: 'region', value: r })),
				...FALLBACK_COUNTRIES.filter(c => c.toLowerCase().includes(lower)).map(c => ({ type: 'country', value: c })),
				...FALLBACK_CITIES.filter(c => c.toLowerCase().includes(lower)).map(c => ({ type: 'city', value: c })),
				...FALLBACK_HOTELS.filter(h => h.toLowerCase().includes(lower)).map(h => ({ type: 'hotel', value: h }))
			].slice(0, 8);
			this.setState({ filteredSuggestions: suggestions, showSuggestions: suggestions.length > 0 });
		}
	};

	selectSuggestion = (suggestion) => {
		const { data, onChange } = this.props;
		const locations = { ...(data.locations || {}) };

		if (suggestion.type === 'region') {
			const regions = locations.regions || [];
			if (!regions.includes(suggestion.value)) {
				onChange('locations', { ...locations, regions: [...regions, suggestion.value] });
			}
		} else if (suggestion.type === 'country') {
			const countries = locations.countries || [];
			if (!countries.includes(suggestion.value)) {
				onChange('locations', { ...locations, countries: [...countries, suggestion.value] });
			}
		} else if (suggestion.type === 'hotel') {
			const hotels = locations.hotels || [];
			if (!hotels.includes(suggestion.value)) {
				onChange('locations', { ...locations, hotels: [...hotels, suggestion.value] });
			}
		} else {
			// city or anything else
			const cities = locations.cities || [];
			if (!cities.includes(suggestion.value)) {
				onChange('locations', { ...locations, cities: [...cities, suggestion.value] });
			}
		}

		this.setState({ searchTerm: '', showSuggestions: false, filteredSuggestions: [] });
	};

	handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const term = this.state.searchTerm.trim();
			if (term) {
				// Pick the top suggestion if there is one; otherwise add as city
				const top = this.state.filteredSuggestions[0];
				this.selectSuggestion(top || { type: 'city', value: term });
			}
		}
	};

	removeLocation = (type, value) => {
		const { data, onChange } = this.props;
		const locations = { ...(data.locations || {}) };
		onChange('locations', {
			...locations,
			[type]: (locations[type] || []).filter(v => v !== value)
		});
	};

	render() {
		const { data, hideTitle } = this.props;
		const { searchTerm, showSuggestions, filteredSuggestions, apiError } = this.state;
		const locations = data.locations || {};
		const regions = locations.regions || [];
		const countries = locations.countries || [];
		const cities = locations.cities || [];
		const hotels = locations.hotels || [];

		const hasSelections = regions.length > 0 || countries.length > 0 || cities.length > 0 || hotels.length > 0;

		return (
			<div className="rfp-step-content">
				{!hideTitle && <h1 className="rfp-step-title">Locations</h1>}

				<p className="rfp-field-label" style={{ fontWeight: 600, marginBottom: 16 }}>
					Add preferred locations for your event.
				</p>

				{apiError && (
					<p style={{ fontSize: 12, color: '#d63939', marginBottom: 8 }}>
						<i className="las la-exclamation-circle" />{' '}
						Could not load location search. You can still type a location and press Enter.
					</p>
				)}

				{/* Search Input */}
				<div className="rfp-location-search-container">
					<input
						className="rfp-location-search"
						type="text"
						value={searchTerm}
						onChange={this.handleSearch}
						onKeyDown={this.handleKeyDown}
						onFocus={() => searchTerm && this.setState({ showSuggestions: true })}
						onBlur={() => setTimeout(() => this.setState({ showSuggestions: false }), 200)}
						placeholder="Search by region, country, city, or hotel"
						autoComplete="off"
					/>
					<div className="rfp-location-underline" />

					{showSuggestions && filteredSuggestions.length > 0 && (
						<div className="rfp-location-suggestions">
							{filteredSuggestions.map((suggestion, index) => (
								<div
									key={suggestion.placeId || index}
									className="rfp-location-suggestion-item"
									onMouseDown={() => this.selectSuggestion(suggestion)}
								>
									<span className="rfp-suggestion-type">
										{suggestion.type === 'region' ? (
											<i className="las la-globe" title="Region" />
										) : suggestion.type === 'country' ? (
											<i className="las la-flag" title="Country" />
										) : suggestion.type === 'hotel' ? (
											<i className="las la-hotel" title="Hotel" />
										) : (
											<i className="las la-city" title="City" />
										)}
										{suggestion.type}
									</span>
									<span className="rfp-suggestion-value">{suggestion.value}</span>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Selected Locations */}
				{hasSelections && (
					<div className="rfp-location-tags-container">
						{regions.length > 0 && (
							<div className="rfp-location-tag-group">
								<div className="rfp-location-tag-group-label">Region</div>
								<div className="rfp-location-tags">
									{regions.map((region) => (
										<div key={region} className="rfp-location-tag">
											<i className="las la-globe" style={{ marginRight: 5, fontSize: 13, opacity: 0.6 }} />
											{region}
											<button
												className="rfp-tag-remove"
												onClick={() => this.removeLocation('regions', region)}
											>
												<i className="las la-times" />
											</button>
										</div>
									))}
								</div>
							</div>
						)}

						{countries.length > 0 && (
							<div className="rfp-location-tag-group">
								<div className="rfp-location-tag-group-label">Country</div>
								<div className="rfp-location-tags">
									{countries.map((country) => (
										<div key={country} className="rfp-location-tag">
											<i className="las la-flag" style={{ marginRight: 5, fontSize: 13, opacity: 0.6 }} />
											{country}
											<button
												className="rfp-tag-remove"
												onClick={() => this.removeLocation('countries', country)}
											>
												<i className="las la-times" />
											</button>
										</div>
									))}
								</div>
							</div>
						)}

						{cities.length > 0 && (
							<div className="rfp-location-tag-group">
								<div className="rfp-location-tag-group-label">City</div>
								<div className="rfp-location-tags">
									{cities.map((city) => (
										<div key={city} className="rfp-location-tag">
											<i className="las la-city" style={{ marginRight: 5, fontSize: 13, opacity: 0.6 }} />
											{city}
											<button
												className="rfp-tag-remove"
												onClick={() => this.removeLocation('cities', city)}
											>
												<i className="las la-times" />
											</button>
										</div>
									))}
								</div>
							</div>
						)}

						{hotels.length > 0 && (
							<div className="rfp-location-tag-group">
								<div className="rfp-location-tag-group-label">Hotel</div>
								<div className="rfp-location-tags">
									{hotels.map((hotel) => (
										<div key={hotel} className="rfp-location-tag">
											<i className="las la-hotel" style={{ marginRight: 5, fontSize: 13, opacity: 0.6 }} />
											{hotel}
											<button
												className="rfp-tag-remove"
												onClick={() => this.removeLocation('hotels', hotel)}
											>
												<i className="las la-times" />
											</button>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

/* ─── Fallback data (used when no API key is configured / always merged) ─── */
const FALLBACK_REGIONS = [
	'North America', 'Europe', 'Asia Pacific', 'Latin America & Caribbean',
	'Middle East & Africa', 'Southeast Asia', 'Scandinavia', 'Caribbean',
	'Sub-Saharan Africa', 'Eastern Europe', 'Western Europe', 'Central America'
];

const FALLBACK_COUNTRIES = [
	// Americas
	'United States', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Colombia',
	'Chile', 'Peru', 'Venezuela', 'Ecuador', 'Bolivia', 'Paraguay', 'Uruguay',
	'Costa Rica', 'Panama', 'Guatemala', 'Cuba', 'Dominican Republic', 'Jamaica',
	// Europe
	'United Kingdom', 'France', 'Germany', 'Spain', 'Italy', 'Netherlands',
	'Portugal', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway',
	'Denmark', 'Finland', 'Poland', 'Czech Republic', 'Hungary', 'Romania',
	'Greece', 'Turkey', 'Ireland', 'Croatia', 'Slovakia', 'Bulgaria',
	// Asia
	'Japan', 'China', 'India', 'South Korea', 'Singapore', 'Thailand',
	'Indonesia', 'Malaysia', 'Philippines', 'Vietnam', 'Hong Kong', 'Taiwan',
	'Bangladesh', 'Pakistan', 'Sri Lanka', 'Nepal', 'Myanmar',
	// Middle East
	'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain',
	'Oman', 'Jordan', 'Lebanon', 'Israel', 'Egypt',
	// Africa
	'South Africa', 'Nigeria', 'Kenya', 'Ghana', 'Ethiopia', 'Morocco',
	'Tanzania', 'Uganda', 'Cameroon', 'Senegal',
	// Oceania
	'Australia', 'New Zealand', 'Fiji', 'Papua New Guinea'
];

const FALLBACK_CITIES = [
	'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'San Diego, CA', 'Las Vegas, NV',
	'Miami, FL', 'Dallas, TX', 'San Francisco, CA', 'Seattle, WA', 'Boston, MA',
	'Orlando, FL', 'Nashville, TN', 'Denver, CO', 'Austin, TX', 'Newport Beach, CA',
	'London, UK', 'Paris, France', 'Barcelona, Spain', 'Tokyo, Japan', 'Dubai, UAE'
];


const FALLBACK_HOTELS = [
	'Marriott Marquis San Diego Marina', 'Hilton San Francisco Union Square',
	'The Venetian Resort Las Vegas', 'JW Marriott Austin', 'InterContinental Miami'
];

export default LocationsStep;
